import { Admin } from '@models/Admin'
import { adminSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createAdmin as createAdminAPI,
  deleteAdmin as deleteAdminAPI,
  fetchAdminById,
  fetchAdmins,
  updateAdmin as updateAdminAPI,
} from '@services/AdminService'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { AppDispatch, RootState } from './store'
const initialState: SBState<Admin> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'admin',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getAdminsSuccess,
  setQuery: setAdminQuery,
  reset: resetAdminState,
  resetQueryAndIds: resetAdminQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Admin }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectAdmins = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedAdmin = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectAdminById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectAdminsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedAdmin =
  (admin: Admin | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Admin>(admin)))

export const getAdmins = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchAdmins(params),
    [adminSchema],
    slice.actions
  )

export const createAdmin = (admin: Admin) =>
  SBAPI.CreateDispatched(createAdminAPI(admin), adminSchema, slice.actions)

export const getAdminWithId = (id: number) =>
  SBAPI.FetchDispatched<Admin>(fetchAdminById(id), adminSchema, slice.actions)

export const updateAdmin = (admin: Admin) =>
  SBAPI.UpdateDispatched<Admin>(updateAdminAPI(admin), admin, slice.actions)

export const deleteAdmin = (admin: Admin) =>
  SBAPI.DestroyDispatched<Admin>(deleteAdminAPI(admin), admin, slice.actions)
